import React, { Component } from 'react';
import CustomModal from '../../../util_components/custom_modal';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format_year, time_format } from '../../../../constants';
import { utc_window } from './filters';
import {
  step_label,
  category_label,
  origin_label,
  origin_tone,
  category_tone,
  tone_style,
  fmt_amount,
  fmt_datetime,
  reason_label,
  student_label
} from './helpers';

// PROD-9152: every event behind one list row. A single failure can be seen by more than one writer
// — our servers, the Stripe webhook and the browser beacon — so each gets its own colour.
function Badge({ tone, children }) {
  return <span style={tone_style(tone)}>{children}</span>;
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-label)' }}>
        {label}
      </div>
      <div style={{ color: 'var(--text-primary)' }}>{children}</div>
    </div>
  );
}

// textAlign is explicit: the global table CSS centres td, which left the body out of step with
// the header.
const cell = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-default)',
  verticalAlign: 'top',
  textAlign: 'left'
};
const head = {
  ...cell,
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--text-label)'
};

// Fixed widths stop Time and Step wrapping and give Reason the slack instead.
const WIDTHS = ['90px', '150px', '110px', 'auto', '120px', '170px'];

class EventsModal extends Component {
  state = { loading: true, error: false, events: [] };

  componentDidMount() {
    const { group, date_filter_type, start_date, end_date, iana_timezone } = this.props;

    // The same window the list used. The group key is an expression over four columns, so no index
    // can serve it — without a date bound this query scans the whole table on every icon click.
    const window = utc_window(date_filter_type, start_date, end_date, iana_timezone);
    const qp = new URLSearchParams();
    qp.set('start_date', window.start);
    qp.set('end_date', window.end);

    const url = get_api_url(
      `/v2/cms_user/payment_failures/${encodeURIComponent(group.group_id)}/events?${qp.toString()}`,
      false,
      true
    );

    get_api(url, true)
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        this.setState({ loading: false, error: false, events: data.events || [] });
      })
      .catch((e) => {
        let message;
        try {
          message = e.response.data.message;
        } catch (err) {
          message = 'Some error occured. Please contact dev team.';
        }
        if (typeof message !== 'string') message = 'Some error occured. Please contact dev team.';
        if (this.props.set_notification_variable) {
          this.props.set_notification_variable(true, 'error', message);
        }
        // Distinct from an empty list: "no events" would be a lie when the fetch never landed.
        this.setState({ loading: false, error: true, events: [] });
      });
  }

  // The ids differ per event; showing whichever one this event carries keeps the column narrow.
  stripe_ref = (event) =>
    event.stripe_payment_intent_id || event.stripe_setup_intent_id || event.stripe_charge_id || '';

  render_row = (event) => (
    <tr key={event.id}>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        {fmt_datetime(event.occurred_at, this.props.iana_timezone, time_format)}
      </td>
      <td style={cell}>{step_label(event.step)}</td>
      <td style={cell}>
        <Badge tone={origin_tone(event.origin)}>{origin_label(event.origin)}</Badge>
      </td>
      <td style={cell}>
        <div style={{ color: 'var(--text-primary)' }}>{reason_label(event)}</div>
        {event.error_message ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{event.error_message}</div>
        ) : null}
      </td>
      <td style={cell}>
        <Badge tone={category_tone(event.error_category)}>
          {category_label(event.error_category)}
        </Badge>
      </td>
      <td style={{ ...cell, fontSize: 12, color: 'var(--text-muted)' }}>
        {event.error_type ? <div>{event.error_type}</div> : null}
        {event.network_decline_code ? <div>network {event.network_decline_code}</div> : null}
        {event.advice_code ? <div>{event.advice_code}</div> : null}
        {this.stripe_ref(event) ? <div>{this.stripe_ref(event)}</div> : null}
      </td>
    </tr>
  );

  render() {
    const { group, close } = this.props;
    const { loading, error, events } = this.state;

    return (
      <CustomModal show close={close}>
        <div style={{ minWidth: 720, maxWidth: 980, color: 'var(--text-primary)' }}>
          <h4 style={{ marginBottom: 12 }}>Payment attempt</h4>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              padding: 12,
              marginBottom: 16,
              background: 'var(--surface-sunken)',
              borderRadius: 6
            }}
          >
            <Field label="Student">{student_label(group) || 'Not resolved'}</Field>
            <Field label="Amount">{fmt_amount(group.amount, group.currency)}</Field>
            <Field label="Country">{group.country || '—'}</Field>
            <Field label="First attempt">
              {fmt_datetime(group.first_attempt, this.props.iana_timezone, day_format_year)}{' '}
              {fmt_datetime(group.first_attempt, this.props.iana_timezone, time_format)}
            </Field>
            <Field label="Reference">
              <span style={{ fontSize: 12 }}>{group.group_id}</span>
            </Field>
          </div>

          {loading ? (
            <div>Loading…</div>
          ) : error ? (
            <div style={{ color: 'var(--state-error-text)' }}>
              Could not load the events for this attempt. This is a load error, not an empty
              timeline.
            </div>
          ) : events.length === 0 ? (
            <div>No events found for this attempt.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {events.length} event{events.length === 1 ? '' : 's'}, oldest first
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  {WIDTHS.map((w, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th style={head}>Time</th>
                    <th style={head}>Step</th>
                    <th style={head}>Seen by</th>
                    <th style={head}>Reason</th>
                    <th style={head}>Category</th>
                    <th style={head}>Details</th>
                  </tr>
                </thead>
                <tbody>{events.map(this.render_row)}</tbody>
              </table>
            </>
          )}
        </div>
      </CustomModal>
    );
  }
}

export default EventsModal;
